/**
 * Deterministic seeds for hand-drawn (rough.js) rendering.
 *
 * Each diagram element gets its own stable wobble by seeding the rough
 * generator with a hash of the element id. The hash must be stable across
 * renders, page loads, and platforms so visual regression baselines and
 * metric-driven re-renders never jitter.
 */

/**
 * Hash a string to a positive integer suitable as a rough.js seed.
 *
 * Uses the FNV-1a 32-bit hash. rough.js treats a seed of 0 as "unseeded"
 * (random), so the result is always >= 1.
 */
export function seedFromId(id: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    hash ^= id.charCodeAt(i);
    // FNV prime multiply, kept in uint32 range via Math.imul.
    hash = Math.imul(hash, 0x01000193);
  }
  // Force into [1, 2^31): positive, never the rough.js "unseeded" 0, and
  // never 2^31 (≡ 0 mod 2^31 degenerates rough.js's MINSTD PRNG).
  return Math.min((hash >>> 1) + 1, 0x7fffffff);
}
